import { SmsLinkCallback } from "./pages/authentication/SmsLinkCallback";
import SecurityPage from './pages/authentication/SecurityPage';
import { AuthProfilePrompt } from './pages/authentication/components/AuthProfilePrompt';
import { EmailLinkCallback } from "./pages/authentication/EmailLinkCallback";
import React, { Suspense } from "react";
import { ChakraProvider, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import LoginPage from "./pages/authentication/LoginPage";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations — Shelv plus the platform pieces it needs
import clientsModuleConfig from "./pages/clients/moduleConfig";
import mediaCDNModuleConfig from "./pages/media-cdn/moduleConfig";
import seoMetadataModuleConfig from "./pages/seometadata/moduleConfig";
import shelvModuleConfig from "./pages/shelv/moduleConfig";
import { shelvPreviewForPath } from "./pages/shelv/previews";

const modules = [
  shelvModuleConfig, // Shelv - restaurant recipe book (free) + Shelv Pro (paid)
  clientsModuleConfig, // Clients - the people who sign in to the site
  mediaCDNModuleConfig, // Media CDN - file hosting with IPFS storage
  seoMetadataModuleConfig, // SEO Manager - page titles, descriptions, social sharing
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ModulePageLayout } from "./components/ModulePageLayout";

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Shelv Pro checkout — the one billing path (see pages/shelv/ProLocked.tsx)
const SubscribePage = React.lazy(() => import("./pages/subscribe/SubscribePage"));
// Public invite landing — deliberately NOT in shelv's moduleConfig.routes,
// which assumes a login and permissionLevel gating. See that module's
// ShelvInviteLanding.tsx header and how-to-build-new-module.md § invites.
const ShelvInviteLanding = React.lazy(() => import("./pages/shelv/ShelvInviteLanding"));

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";

// Profile-related imports
import ViewProfile from "./pages/profile/ViewProfile";
import EditProfile from "./pages/profile/EditProfile";
import Settings from "./pages/profile/Settings";
import ShippingAddresses from "./pages/profile/ShippingAddresses";
import ProfilePaymentMethods from "./pages/profile/PaymentMethods";
import SubscriptionOffers from "./pages/profile/SubscriptionOffers";
import CurrentSubscriptions from "./pages/profile/CurrentSubscriptions";

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";
import { useAuth } from "./contexts/AuthContext";


/**
 * Above-the-fold teasers for gated routes, keyed by module id.
 *
 * A module owns its own teasers in a module-local `previews.ts` (so they
 * travel with Module Sync) and joins here with one line. Deliberately a
 * registry rather than a field on the shared `ModuleConfig` type: that type is
 * cross-declared in seven modules' `### Shared files` tables across nineteen
 * repos, so adding a field there drags every one of them through the Module
 * Sync conflict gate for no benefit.
 *
 * A module with no entry — or a route with no teaser — falls back to the bare
 * "Sign in to continue", which is correct for pages nobody shares a link to.
 */
const ROUTE_PREVIEWS: Record<string, (path: string) => { title: string; blurb: string } | undefined> = {
  shelv: shelvPreviewForPath,
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();

  // Resolve permissionLevel → actual permission strings from backend
  // Modules using the new pattern have permissionLevel on routes/nav and empty permission arrays.
  // Modules not yet migrated still have hardcoded permission arrays — those pass through unchanged.
  const resolvedModules = React.useMemo(() => {
    return modules.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module; // Not migrated yet — use hardcoded permissions

      return {
        ...module,
        routes: module.routes?.map((route: any) => {
          const level = (route as any).permissionLevel;
          if (!level) return route; // No permissionLevel — use existing permissions array
          return { ...route, permissions: (modulePerms as any)[level] || [] };
        }),
        navigation: module.navigation?.map((navItem: any) => {
          const level = (navItem as any).permissionLevel;
          if (!level) return navItem; // No permissionLevel — use existing permissions array
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions.view,
          create: modulePerms.create || module.permissions.create,
          edit: modulePerms.edit || module.permissions.edit,
          delete: modulePerms.delete || module.permissions.delete,
          ...(modulePerms.financial ? { financial: modulePerms.financial } : {}),
        },
      };
    });
  }, [backendPerms]);

  return (
    <>
      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar modules={resolvedModules} />}

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <Routes>
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Login page */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/security" element={<ProtectedRoute requiredPermissions={[]}><SecurityPage /></ProtectedRoute>} />
        <Route path="/auth/sms-link" element={<SmsLinkCallback />} />
        <Route path="/auth/email-link" element={<EmailLinkCallback defaultPath="/shelv" />} />

        {/* Public pages (no login needed): the Shelv Pro checkout and invite links */}
        <Route path="/subscribe/:slug" element={<Suspense fallback={<LoadingFallback />}><SubscribePage /></Suspense>} />
        <Route path="/shelv/invite/:slug" element={<Suspense fallback={<LoadingFallback />}><ShelvInviteLanding /></Suspense>} />

        {/* Dynamic Module Routes — permissions resolved from backend for migrated modules */}
        {resolvedModules.map((module) =>
          module.routes?.map((route: any) => {
            const pageComponent = (
              <Suspense fallback={<LoadingFallback />}>
                <route.component />
              </Suspense>
            );

            // If useLayout is true, wrap with ModulePageLayout (auto Navbar + Breadcrumb + Container + Footer)
            const content = route.useLayout ? (
              <ModulePageLayout
                moduleConfig={module}
                {...(route.layoutOptions || {})}
              >
                {pageComponent}
              </ModulePageLayout>
            ) : pageComponent;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  (route as any).public ? (
                    content
                  ) : (
                    <ProtectedRoute
                      requiredPermissions={route.permissions || []}
                      preview={ROUTE_PREVIEWS[module.id]?.(route.path)}
                    >
                      {content}
                    </ProtectedRoute>
                  )
                }
              />
            );
          })
        )}

        {/* Profile routes */}
        <Route path="/profile" element={<ViewProfile />} />
        <Route path="/profile/edit" element={<EditProfile />} />
        <Route path="/profile/settings" element={<Settings />} />
        <Route path="/profile/shipping" element={<ShippingAddresses />} />
        <Route path="/profile/payment-methods" element={<ProfilePaymentMethods />} />
        <Route path="/profile/subscription-offers" element={<SubscriptionOffers />} />
        <Route path="/profile/subscriptions" element={<CurrentSubscriptions />} />

        {/* Any other address (an old link to a module this repo no longer has) goes to the shelf */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      // Use the tenantId from brandConfig as default
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
          <AuthProvider>
            <ModulePermissionsProvider>
              <ChakraProvider theme={theme}>
                <AuthProfilePrompt />
                <AppContent />
              </ChakraProvider>
            </ModulePermissionsProvider>
          </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
